import { cn } from '../../lib/cn.js'
import { ChevronLeft, MoreHorizontal } from 'lucide-react'
import { Row, Stack } from './Stack.js'
import { Text } from '../ui/Typography.js'
import { Tooltip } from '../ui/Tooltip.js'
import { Button } from '../ui/Button.js'
import { DropdownMenu, type MenuItem } from '../ui/DropdownMenu.js'

export interface PageHeaderProps {
  title: React.ReactNode
  /** The line under the title. What this page is showing, and of what. */
  subtitle?: React.ReactNode
  /**
   * The search box, which belongs up here beside the action.
   *
   * The kit and the working prototype both put it in the head — 220px, to the
   * left of the page's one button. It was in a bar of its own underneath,
   * which split "what this page is" from "how you narrow it" across two rows
   * for no reason.
   */
  search?: React.ReactNode
  /**
   * The way out of a record, as an icon to the left of the title.
   *
   * It was `BackLink`: its own row above the header, reading "Back to
   * Providers" in full, stretched the width of the page, on seven screens. A
   * record's title already says what you are looking at, so the way back needs
   * to be a control and not a sentence.
   */
  onBack?: () => void
  /** Where it goes, for the label a screen reader reads. */
  backTo?: string
  /**
   * The things you can do to this record, behind one control.
   *
   * Record headers carried them as full text buttons — "Their appointments"
   * at 162px beside "Suspend this account" at 176px, both 48px tall, which is
   * a third of the header spent on two things somebody might never press. And
   * every one of them was already in the row's own `⋮` on the table behind,
   * in a different shape: two ideas for one thing.
   *
   * One idea now. What stays a **button** is an action that is the reason the
   * page was opened — starting a class, exporting a record — and those are
   * passed as `actions` as before.
   */
  menu?: MenuItem[]
  /** Buttons on the right of the title. Never more than two. */
  actions?: React.ReactNode
}

/**
 * The top of every screen. One shape, so no two pages introduce themselves
 * differently.
 *
 * Built to `.fawn-desk__head`, which is the whole of it:
 *
 * ```css
 * .fawn-desk__head { display:flex; align-items:center; justify-content:space-between;
 *                    gap: var(--fawn-space-6); }
 * ```
 *
 * Two things were wrong and both were invented here rather than taken from
 * the kit. There was a **`border-b` under every page**, which the kit does not
 * have and which put a rule between the title and the first card on every
 * screen — the client called the header borders bad before they could say why.
 * And it aligned to the **top**, so a page with a subtitle sat its buttons
 * level with the title while a page without one sat them level with nothing.
 * The kit centres them.
 */
export function PageHeader({
  title,
  subtitle,
  search,
  onBack,
  backTo,
  menu,
  actions,
}: PageHeaderProps) {
  return (
    /**
     * Stacked on a phone, side by side from `sm` up.
     *
     * The title and the controls shared one row at every width. At 390px that
     * left the subtitle about ninety pixels — *"Everyone listed, and everyone
     * who has not finished their details"* came out as a column of single
     * words down the left edge while the search box sat beside it.
     *
     * `items-start` rather than `center` when stacked, or the controls centre
     * themselves under a two-line title and read as belonging to nothing.
     */
    <div
      className={cn(
        'flex gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-6',
        /*
         * Stacked only when the controls are wide enough to need a line.
         *
         * A search box or a real button — *Start the class*, *Publish* — is
         * wide, and sharing a row with a heading left the class console's
         * title *"Getting your money in order after a separation"* as three
         * lines in a sliver beside it.
         *
         * A bare ⋯ is forty pixels and stays on the title's row, because
         * giving it a full-width line of its own put fifty pixels of nothing
         * into every record on the desk. Two different controls, two answers.
         */
        search || actions ? 'flex-col items-start' : 'flex-row items-start',
      )}
    >
      <Row gap={3} className={cn('min-w-0', search || actions ? 'w-full sm:w-auto' : 'flex-1')}>
        {onBack && (
          <Tooltip label={backTo ? `Back to ${backTo}` : 'Back'}>
            <button
              type="button"
              aria-label={backTo ? `Back to ${backTo}` : 'Back'}
              onClick={onBack}
              className="border-hairline bg-surface text-ink-muted hover:text-ink hover:border-hairline-strong focus-visible:ring-coral-fill/30 grid size-9 shrink-0 place-items-center rounded-md border outline-none transition-colors focus-visible:ring-2 [&_svg]:size-4"
            >
              <ChevronLeft />
            </button>
          </Tooltip>
        )}
        <Stack gap={0} className="min-w-0">
          <Text as="h1" variant="title">
            {title}
          </Text>
          {/* 2px under the title, which is what the prototype sets. */}
          {subtitle && (
            <Text variant="secondary" className="mt-0.5">
              {subtitle}
            </Text>
          )}
        </Stack>
      </Row>
      {(search || actions || menu?.length) && (
        /**
         * One row on a phone: the search stretches, the buttons do not.
         *
         * The buttons here are `compact`, so below `sm` they are an icon
         * each — about forty pixels — and two of them fit beside a search box
         * easily. Said directly: *"when they are icon only they can fit
         * beside the search bar easily so search bar can stretch the rest of
         * the space even with two buttons like export and invite beside
         * it."*
         *
         * So no wrapping and no full-width search: the search takes what is
         * left, which is what `flex-1` on it below means.
         */
        <Row
          gap={2}
          className={cn(
            'sm:w-auto sm:shrink-0 sm:gap-3',
            /* A full row only when there is a search box to fill it. A record
               screen's controls are one ⋯ menu, and giving that its own
               full-width row put fifty pixels of nothing between the title
               and the record on every record on the desk. */
            search || actions ? 'w-full' : 'w-auto',
          )}
        >
          {/* The one thing that grows. `min-w-0`, or a flex item refuses to
              shrink below its content and pushes the buttons off the edge. */}
          {search && <div className="min-w-0 flex-1 sm:flex-none">{search}</div>}
          {actions}
          {menu && menu.length > 0 && (
            <DropdownMenu
              items={menu}
              trigger={
                <Button variant="secondary" size="icon" aria-label="What you can do with this">
                  <MoreHorizontal />
                </Button>
              }
            />
          )}
        </Row>
      )}
    </div>
  )
}
